import React from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Input from "../../../component-lib/Atoms/Input/Input";
import Button from "../../../component-lib/Molecules/button/Button";
import ContactInput from "../../../component-lib/Atoms/Input/ContactInput";
import {
  ADD_NEW_PATIENT_DATA,
  EMERGENCY_CONTACT_RELATIONS,
  GENDER,
} from "../constants";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import DatePicker from "../../../component-lib/Atoms/DatePicker/DatePicker";
import moment from "moment";
import { VALIDATION_CONSTANTS } from "../../../utils/validation-constants/validationConstants";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import { useDispatch } from "react-redux";
import { createNewPatient } from "../FacilityManagementSaga";
import { useNavigate, useParams } from "react-router-dom";
import BootstrapDialog from "../../../utils/bootstrap-dialog-style/BootstrapDialog";
import UnitNumberSelect from "../../../component-lib/Molecules/unit-number-select/UnitNumberSelect";

interface AddNewPatientDialogProps {
  isOpen: boolean;
  handlePatientFieldsData: (data: any) => void;
  handleClose: () => void;
}

export default function AddNewPatientDialog({
  isOpen = false,
  handleClose,
}: AddNewPatientDialogProps) {
  const { facilityId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const headerStyle = {
    fontWeight: "500",
    fontSize: "16px",
  };
  const validationSchema = Yup.object().shape({
    [ADD_NEW_PATIENT_DATA.FIRST_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER,
        "First name can only contain characters"
      )
      .required("First name is required"),
    [ADD_NEW_PATIENT_DATA.LAST_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER,
        "Last name can only contain characters"
      )
      .required("Last name is required"),

    [ADD_NEW_PATIENT_DATA.BSA_ID]: Yup.number().required("BSA ID is required"),
    [ADD_NEW_PATIENT_DATA.DOB]: Yup.date()
      .max(new Date(), "Date of birth cannot be a future date")
      .required("Date Of birth is required"),
    [ADD_NEW_PATIENT_DATA.CONTACT_NUMBER]: Yup.string()
      .matches(VALIDATION_CONSTANTS.CONTACT_NUMBER, "Invalid contact number")
      .required("Contact number is required"),
    [ADD_NEW_PATIENT_DATA.GENDER]: Yup.string().required("Gender is required"),
    // [ADD_NEW_PATIENT_DATA.JAMBOREE_UNIT_NUMBER]: Yup.string()
    //   .matches(
    //     VALIDATION_CONSTANTS.ID,
    //     "Jamboree unit number must contain only letters and numbers"
    //   )
    //   .required("Jamboree unit number is required"),

    // [ADD_NEW_PATIENT_DATA.JAMBOREE_UNIT_LEADER_NAME]: Yup.string()
    //   .matches(
    //     VALIDATION_CONSTANTS.NAME,
    //     "Jamboree unit leader name can only contain alphanumeric characters"
    //   )
    //   .required("Jamboree unit leader name is required"),

    [ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER,
        "Emergency contact name can only contain characters"
      )
      .required("Emergency contact name is required"),
    [ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_RELATION]: Yup.string().required(
      "Emergency contact relation is required"
    ),
    [ADD_NEW_PATIENT_DATA.EMERGENCY_EMAIL]: Yup.string()
      // .email("Enter a valid email address")
      .matches(VALIDATION_CONSTANTS.EMAIL, "Enter a valid email address")
      .required("Email is required"),
    [ADD_NEW_PATIENT_DATA.EMAIL]: Yup.string()
      // .email("Enter a valid email address")
      .matches(VALIDATION_CONSTANTS.EMAIL, "Enter a valid email address")
      .required("Email is required"),
    [ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_NUMBER]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CONTACT_NUMBER,
        "Invalid emergency contact number"
      )
      .required("Emergency contact number is required"),
  });

  const handleNewPatientData = (values: any) => {
    const payload = {
      apiPayload: {
        firstName: values?.firstName,
        lastName: values?.lastName,
        bsaId: values?.bsaId,
        email: values?.email,
        dateOfBirth: moment(values?.dateOfBirth)?.format("YYYY-MM-DD"),
        contactNumber: values?.contact,
        gender: values?.gender,
        campId: values?.clinicLocation,
        emergencyContact: {
          contactName: values?.emergencyContactName,
          contactRelation: values?.emergencyContactRelation,
          contactEmail: values?.emergencyEmail,
          contactNumber: values?.emergencyContactNumber,
        },
        status: "Active",
        facilityId: facilityId,
      },
      navigate,
      comesFrom: "addPatient",
    };
    dispatch(createNewPatient(payload));
  };

  return (
    <BootstrapDialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
    >
      <Formik
        initialValues={{
          [ADD_NEW_PATIENT_DATA.FIRST_NAME]: "",
          [ADD_NEW_PATIENT_DATA.LAST_NAME]: "",

          [ADD_NEW_PATIENT_DATA.BSA_ID]: "",
          [ADD_NEW_PATIENT_DATA.DOB]: undefined,
          [ADD_NEW_PATIENT_DATA.CONTACT_NUMBER]: "",

          [ADD_NEW_PATIENT_DATA.GENDER]: "",
          [ADD_NEW_PATIENT_DATA.JAMBOREE_UNIT_NUMBER]: "",

          [ADD_NEW_PATIENT_DATA.JAMBOREE_UNIT_LEADER_NAME]: "",
          [ADD_NEW_PATIENT_DATA.JAMBOREE_UNIT_LEADER_EMAIL]: "",
          [ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_NAME]: "",
          [ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_RELATION]: "",
          [ADD_NEW_PATIENT_DATA.CLINIC_LOCATION]: "",
          [ADD_NEW_PATIENT_DATA.EMERGENCY_EMAIL]: "",
          [ADD_NEW_PATIENT_DATA.EMAIL]: "",

          [ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_NUMBER]: "",
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(formVals) => {
          handleNewPatientData(formVals);
          try {
          } catch (error) {
            console.log(error);
          }
        }}
      >
        {({
          errors,
          touched,
          submitForm,
          isSubmitting,
          handleReset,
          handleBlur,
          values,
          setFieldValue,
          handleSubmit,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  fontFamily: "inter",
                }}
                id="customized-dialog-title"
              >
                Add New Patient
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <hr />
              <DialogContent>
                <div className="w-full flex flex-col gap-6">
                  <div className="flex flex-col gap-4 pt-3">
                    <Heading
                      title="Demographic Details"
                      customStyle={headerStyle}
                    />

                    <div className="flex gap-4">
                      <div className="flex-1">
                        <Input
                          textFieldLabel="First Name"
                          name={ADD_NEW_PATIENT_DATA.FIRST_NAME}
                          value={values[ADD_NEW_PATIENT_DATA.FIRST_NAME]}
                          onChangeCb={(e) => {
                            setFieldValue(
                              ADD_NEW_PATIENT_DATA.FIRST_NAME,
                              e.target.value
                            );
                          }}
                          onBlur={handleBlur}
                          size="small"
                          error={
                            touched[ADD_NEW_PATIENT_DATA.FIRST_NAME] &&
                            Boolean(errors[ADD_NEW_PATIENT_DATA.FIRST_NAME])
                          }
                          helperText={
                            touched[ADD_NEW_PATIENT_DATA.FIRST_NAME]
                              ? errors[ADD_NEW_PATIENT_DATA.FIRST_NAME]
                              : undefined
                          }
                        />
                      </div>

                      <div className="flex-1">
                        <Input
                          textFieldLabel="Last Name"
                          name={ADD_NEW_PATIENT_DATA.LAST_NAME}
                          value={values[ADD_NEW_PATIENT_DATA.LAST_NAME]}
                          onChangeCb={(e) => {
                            setFieldValue(
                              ADD_NEW_PATIENT_DATA.LAST_NAME,
                              e.target.value
                            );
                          }}
                          onBlur={handleBlur}
                          size="small"
                          error={
                            touched[ADD_NEW_PATIENT_DATA.LAST_NAME] &&
                            Boolean(errors[ADD_NEW_PATIENT_DATA.LAST_NAME])
                          }
                          helperText={
                            touched[ADD_NEW_PATIENT_DATA.LAST_NAME]
                              ? errors[ADD_NEW_PATIENT_DATA.LAST_NAME]
                              : undefined
                          }
                        />
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-1">
                        <Input
                          textFieldLabel="BSA ID"
                          name={ADD_NEW_PATIENT_DATA.BSA_ID}
                          value={values[ADD_NEW_PATIENT_DATA.BSA_ID]}
                          onChangeCb={(e) => {
                            setFieldValue(
                              ADD_NEW_PATIENT_DATA.BSA_ID,
                              e.target.value
                            );
                          }}
                          type="number"
                          onBlur={handleBlur}
                          size="small"
                          error={
                            touched[ADD_NEW_PATIENT_DATA.BSA_ID] &&
                            Boolean(errors[ADD_NEW_PATIENT_DATA.BSA_ID])
                          }
                          helperText={
                            touched[ADD_NEW_PATIENT_DATA.BSA_ID]
                              ? errors[ADD_NEW_PATIENT_DATA.BSA_ID]
                              : undefined
                          }
                        />
                      </div>

                      <div className="flex-1">
                        <DatePicker
                          name={ADD_NEW_PATIENT_DATA.DOB}
                          value={values[ADD_NEW_PATIENT_DATA.DOB]}
                          onChangeCb={(value) => {
                            setFieldValue(ADD_NEW_PATIENT_DATA.DOB, value);
                          }}
                          label="Date of Birth"
                          style={{ width: "100%" }}
                          error={
                            touched[ADD_NEW_PATIENT_DATA.DOB] &&
                            Boolean(errors[ADD_NEW_PATIENT_DATA.DOB])
                          }
                          helperText={
                            touched[ADD_NEW_PATIENT_DATA.DOB]
                              ? errors[ADD_NEW_PATIENT_DATA.DOB]
                              : undefined
                          }
                          maxDate={moment()}
                        />
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <Input
                          textFieldLabel="Email"
                          name={ADD_NEW_PATIENT_DATA.EMAIL}
                          value={values[ADD_NEW_PATIENT_DATA.EMAIL]}
                          onChangeCb={(e) => {
                            setFieldValue(
                              ADD_NEW_PATIENT_DATA.EMAIL,
                              e.target.value
                            );
                          }}
                          onBlur={handleBlur}
                          size="small"
                          error={
                            touched[ADD_NEW_PATIENT_DATA.EMAIL] &&
                            Boolean(errors[ADD_NEW_PATIENT_DATA.EMAIL])
                          }
                          helperText={
                            touched[ADD_NEW_PATIENT_DATA.EMAIL]
                              ? errors[ADD_NEW_PATIENT_DATA.EMAIL]
                              : undefined
                          }
                        />
                      </div>
                      <div className="flex-1">
                        <ContactInput
                          textFieldLabel="Contact Number"
                          name={ADD_NEW_PATIENT_DATA.CONTACT_NUMBER}
                          value={values[ADD_NEW_PATIENT_DATA.CONTACT_NUMBER]}
                          onChangeCb={(value) => {
                            setFieldValue(
                              ADD_NEW_PATIENT_DATA.CONTACT_NUMBER,
                              value
                            );
                          }}
                          size="small"
                          onBlur={handleBlur}
                          error={
                            touched[ADD_NEW_PATIENT_DATA.CONTACT_NUMBER] &&
                            Boolean(errors[ADD_NEW_PATIENT_DATA.CONTACT_NUMBER])
                          }
                          helperText={
                            touched[ADD_NEW_PATIENT_DATA.CONTACT_NUMBER]
                              ? errors[ADD_NEW_PATIENT_DATA.CONTACT_NUMBER]
                              : undefined
                          }
                          isContactNumber={true}
                        />
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-1">
                        <SelectDropdown
                          label="Gender"
                          name={ADD_NEW_PATIENT_DATA.GENDER}
                          value={values[ADD_NEW_PATIENT_DATA.GENDER]}
                          options={GENDER}
                          onChangeCb={(e) => {
                            setFieldValue(
                              ADD_NEW_PATIENT_DATA.GENDER,
                              e.target.value
                            );
                          }}
                          onBlur={handleBlur}
                          error={
                            touched[ADD_NEW_PATIENT_DATA.GENDER] &&
                            Boolean(errors[ADD_NEW_PATIENT_DATA.GENDER])
                          }
                          helperText={
                            touched[ADD_NEW_PATIENT_DATA.GENDER]
                              ? errors[ADD_NEW_PATIENT_DATA.GENDER]
                              : undefined
                          }
                        />
                      </div>

                      <div className="flex-1">
                        <UnitNumberSelect
                          label="Unit Number"
                          allowCreate
                          facilityId={facilityId}
                          onChange={(record) => {
                            setFieldValue(
                              ADD_NEW_PATIENT_DATA.CLINIC_LOCATION,
                              record?.id || "",
                              true
                            );
                            setFieldValue(
                              ADD_NEW_PATIENT_DATA.JAMBOREE_UNIT_NUMBER,
                              record?.unitNumber || ""
                            );
                            setFieldValue(
                              ADD_NEW_PATIENT_DATA.JAMBOREE_UNIT_LEADER_NAME,
                              record?.leaderName || ""
                            );
                            setFieldValue(
                              ADD_NEW_PATIENT_DATA.JAMBOREE_UNIT_LEADER_EMAIL,
                              record?.email || ""
                            );
                          }}
                        />
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-1">
                        <Input
                          disabled
                          textFieldLabel="Jamboree Unit Leader Name"
                          name={ADD_NEW_PATIENT_DATA.JAMBOREE_UNIT_LEADER_NAME}
                          value={
                            values[
                              ADD_NEW_PATIENT_DATA.JAMBOREE_UNIT_LEADER_NAME
                            ]
                          }
                          onChangeCb={(e) => {
                            setFieldValue(
                              ADD_NEW_PATIENT_DATA.JAMBOREE_UNIT_LEADER_NAME,
                              e.target.value
                            );
                          }}
                          size="small"
                          onBlur={handleBlur}
                          error={Boolean(
                            touched[
                              ADD_NEW_PATIENT_DATA.JAMBOREE_UNIT_LEADER_NAME
                            ] &&
                              errors[
                                ADD_NEW_PATIENT_DATA.JAMBOREE_UNIT_LEADER_NAME
                              ]
                          )}
                          helperText={
                            touched[
                              ADD_NEW_PATIENT_DATA.JAMBOREE_UNIT_LEADER_NAME
                            ]
                              ? errors[
                                  ADD_NEW_PATIENT_DATA.JAMBOREE_UNIT_LEADER_NAME
                                ]
                              : undefined
                          }
                        />
                      </div>
                      <div className="flex-1">
                        <Input
                          disabled
                          textFieldLabel="Jamboree Unit Leader Email"
                          name={ADD_NEW_PATIENT_DATA.JAMBOREE_UNIT_LEADER_EMAIL}
                          value={
                            values[
                              ADD_NEW_PATIENT_DATA.JAMBOREE_UNIT_LEADER_EMAIL
                            ]
                          }
                          onChangeCb={(e) => {
                            setFieldValue(
                              ADD_NEW_PATIENT_DATA.JAMBOREE_UNIT_LEADER_EMAIL,
                              e.target.value
                            );
                          }}
                          size="small"
                          onBlur={handleBlur}
                          error={Boolean(
                            touched[
                              ADD_NEW_PATIENT_DATA.JAMBOREE_UNIT_LEADER_EMAIL
                            ] &&
                              errors[
                                ADD_NEW_PATIENT_DATA.JAMBOREE_UNIT_LEADER_EMAIL
                              ]
                          )}
                          helperText={
                            touched[
                              ADD_NEW_PATIENT_DATA.JAMBOREE_UNIT_LEADER_EMAIL
                            ]
                              ? errors[
                                  ADD_NEW_PATIENT_DATA.JAMBOREE_UNIT_LEADER_EMAIL
                                ]
                              : undefined
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-4">
                    <Heading
                      title="Emergency Contact Details"
                      customStyle={headerStyle}
                    />

                    <div className="flex gap-4">
                      <div className="flex-1">
                        <Input
                          textFieldLabel="Emergency Contact Name"
                          name={ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_NAME}
                          value={
                            values[ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_NAME]
                          }
                          onChangeCb={(e) => {
                            setFieldValue(
                              ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_NAME,
                              e.target.value
                            );
                          }}
                          size="small"
                          onBlur={handleBlur}
                          error={Boolean(
                            touched[
                              ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_NAME
                            ] &&
                              errors[
                                ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_NAME
                              ]
                          )}
                          helperText={
                            touched[ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_NAME]
                              ? errors[
                                  ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_NAME
                                ]
                              : undefined
                          }
                        />
                      </div>
                      <div className="flex-1">
                        <SelectDropdown
                          label="Emergency Contact Relation"
                          name={ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_RELATION}
                          value={
                            values[
                              ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_RELATION
                            ]
                          }
                          options={EMERGENCY_CONTACT_RELATIONS}
                          onChangeCb={(e) =>
                            setFieldValue(
                              ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_RELATION,
                              e.target.value
                            )
                          }
                          onBlur={handleBlur}
                          error={Boolean(
                            touched[
                              ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_RELATION
                            ] &&
                              errors[
                                ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_RELATION
                              ]
                          )}
                          helperText={
                            touched[
                              ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_RELATION
                            ]
                              ? errors[
                                  ADD_NEW_PATIENT_DATA
                                    .EMERGENCY_CONTACT_RELATION
                                ]
                              : undefined
                          }
                        />
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-1">
                        <Input
                          textFieldLabel="Emergency Contact Email"
                          name={ADD_NEW_PATIENT_DATA.EMERGENCY_EMAIL}
                          value={values[ADD_NEW_PATIENT_DATA.EMERGENCY_EMAIL]}
                          onChangeCb={(e) => {
                            setFieldValue(
                              ADD_NEW_PATIENT_DATA.EMERGENCY_EMAIL,
                              e.target.value
                            );
                          }}
                          onBlur={handleBlur}
                          size="small"
                          error={
                            touched[ADD_NEW_PATIENT_DATA.EMERGENCY_EMAIL] &&
                            Boolean(
                              errors[ADD_NEW_PATIENT_DATA.EMERGENCY_EMAIL]
                            )
                          }
                          helperText={
                            touched[ADD_NEW_PATIENT_DATA.EMERGENCY_EMAIL]
                              ? errors[ADD_NEW_PATIENT_DATA.EMERGENCY_EMAIL]
                              : undefined
                          }
                        />
                      </div>
                      <div className="flex-1">
                        <ContactInput
                          textFieldLabel="Emergency Contact Number"
                          name={ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_NUMBER}
                          value={
                            values[
                              ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_NUMBER
                            ]
                          }
                          onChangeCb={(value) => {
                            setFieldValue(
                              ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_NUMBER,
                              value
                            );
                          }}
                          size="small"
                          onBlur={handleBlur}
                          error={
                            touched[
                              ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_NUMBER
                            ] &&
                            Boolean(
                              errors[
                                ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_NUMBER
                              ]
                            )
                          }
                          helperText={
                            touched[
                              ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_NUMBER
                            ]
                              ? errors[
                                  ADD_NEW_PATIENT_DATA.EMERGENCY_CONTACT_NUMBER
                                ]
                              : undefined
                          }
                          isContactNumber={true}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </DialogContent>
              <DialogActions style={{ padding: "24px" }}>
                <Button
                  type="submit"
                  label="Add New Patient"
                  variant="contained"
                  onClickCb={() => {}}
                  style={{
                    padding: "10px 18px",
                  }}
                ></Button>
              </DialogActions>{" "}
            </Form>
          );
        }}
      </Formik>
    </BootstrapDialog>
  );
}
